import { api } from '../client';

export interface MaintenanceSchedule {
  id: string;
  companyId: string;
  name: string;
  equipmentId: string;
  equipment?: {
    id: string;
    name: string;
    assetNumber: string;
    status?: string;
    condition?: string;
  };
  frequency: string;
  intervalDays?: number;
  intervalHours?: number;
  lastPerformed?: string;
  nextDue: string;
  priority: string;
  estimatedDuration?: number;
  instructions?: string;
  checklistItems: string[];
  requiredParts?: RequiredPart[];
  status: string;
  createdById: string;
  createdBy?: {
    id: string;
    fullName: string;
  };
  createdAt: string;
  updatedAt: string;
  _count?: {
    requiredParts: number;
  };
}

export interface RequiredPart {
  id: string;
  maintenanceScheduleId: string;
  itemId: string;
  item?: {
    id: string;
    name: string;
    sku: string;
    unitCost?: number;
    inventoryLevels?: { quantity: number }[];
  };
  quantity: number;
  unit: string;
}

export interface QueryMaintenanceScheduleParams {
  equipmentId?: string;
  frequency?: string;
  priority?: string;
  status?: string;
  dueSoon?: boolean;
  overdue?: boolean;
  dueFrom?: string;
  dueTo?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface CreateMaintenanceScheduleDto {
  name: string;
  equipmentId: string;
  frequency: string;
  intervalDays?: number;
  intervalHours?: number;
  nextDue: string;
  priority?: string;
  estimatedDuration?: number;
  instructions?: string;
  checklistItems?: string[];
}

export interface UpdateMaintenanceScheduleDto extends Partial<CreateMaintenanceScheduleDto> {
  status?: string;
}

export interface AddRequiredPartDto {
  itemId: string;
  quantity: number;
  unit: string;
}

export interface HoursBasedPMStatus {
  scheduleId: string;
  scheduleName: string;
  equipmentId: string;
  equipmentName: string;
  equipmentAssetNumber: string;
  currentHours: number;
  intervalHours: number;
  hoursSinceLastPM: number;
  percentToNextPM: number;
  isDueSoon: boolean;
  isOverdue: boolean;
}

export interface MaintenanceScheduleDashboard {
  total: number;
  active: number;
  dueSoon: number;
  overdue: number;
  byPriority: Record<string, number>;
  byFrequency: Record<string, number>;
  upcomingSchedules: MaintenanceSchedule[];
  autoGeneratedThisMonth: number;
  hoursBasedStatus: HoursBasedPMStatus[];
}

/** Fields a bulk edit may set. Mirrors BulkScheduleChangesDto on the server. */
export interface BulkScheduleChanges {
  status?: string;
  priority?: string;
  frequency?: string;
  intervalDays?: number;
  nextDue?: string;
}

export interface BulkUpdateSchedulesRequest {
  scheduleIds: string[];
  changes: BulkScheduleChanges;
}

export interface BulkUpdateScheduleRowResult {
  id: string;
  name?: string;
  equipmentName?: string;
  status: 'updated' | 'failed';
  reason?: string;
  [key: string]: unknown;
}

export interface BulkUpdateSchedulesResult {
  requested: number;
  updated: number;
  failed: number;
  fields: string[];
  results: BulkUpdateScheduleRowResult[];
}

export const maintenanceScheduleApi = {
  bulkUpdate: async (data: BulkUpdateSchedulesRequest) => {
    const response = await api.post<BulkUpdateSchedulesResult>(
      '/maintenance-schedules/bulk/update',
      data
    );
    return response.data;
  },

  getAll: (params?: QueryMaintenanceScheduleParams) =>
    api.get<{ data: MaintenanceSchedule[]; pagination: any }>('/maintenance-schedules', { params }),

  getById: (id: string) => api.get<MaintenanceSchedule>(`/maintenance-schedules/${id}`),

  getDashboard: () => api.get<MaintenanceScheduleDashboard>('/maintenance-schedules/dashboard'),

  getCalendar: (startDate: string, endDate: string) =>
    api.get<MaintenanceSchedule[]>('/maintenance-schedules/calendar', {
      params: { startDate, endDate },
    }),

  create: (data: CreateMaintenanceScheduleDto) =>
    api.post<MaintenanceSchedule>('/maintenance-schedules', data),

  update: (id: string, data: UpdateMaintenanceScheduleDto) =>
    api.patch<MaintenanceSchedule>(`/maintenance-schedules/${id}`, data),

  delete: (id: string) => api.delete(`/maintenance-schedules/${id}`),

  // Required Parts
  addRequiredPart: (scheduleId: string, data: AddRequiredPartDto) =>
    api.post<RequiredPart>(`/maintenance-schedules/${scheduleId}/parts`, data),

  removeRequiredPart: (scheduleId: string, partId: string) =>
    api.delete(`/maintenance-schedules/${scheduleId}/parts/${partId}`),

  // Work Order Generation
  generateWorkOrder: (scheduleId: string) =>
    api.post(`/maintenance-schedules/${scheduleId}/generate-work-order`),
};

export const MAINTENANCE_FREQUENCIES = [
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'BIWEEKLY', label: 'Bi-Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'SEMI_ANNUAL', label: 'Semi-Annual' },
  { value: 'ANNUAL', label: 'Annual' },
  { value: 'HOURS_BASED', label: 'Hours Based' },
  { value: 'CONDITION_BASED', label: 'Condition Based' },
];

export const MAINTENANCE_PRIORITIES = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'CRITICAL', label: 'Critical' },
];

export const SCHEDULE_STATUSES = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'PAUSED', label: 'Paused' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];
